import type React from 'react';

import { EQ_BAND_FREQUENCIES, EQ_PEAKING_Q, dbToLinear, filterTypeForBand } from './equalizer';
import type { MusicEngine, MusicEngineEvents } from './musicEngineTypes';

/**
 * Движок плеера в браузере: `<audio>` + Web Audio. Граф тот же, что на телефоне
 * (`musicEngine.tsx`): trackGain → [10 фильтров] → master → анализатор → выход. Браузер сам
 * читает файл потоком из `blob:` и сам держит звук в фоновой вкладке.
 */

const FADE_SEC = 0.12;
const FADE_TAU = FADE_SEC / 4;

class WebMusicEngine implements MusicEngine {
  private events: MusicEngineEvents = {};

  setEvents(events: MusicEngineEvents): void {
    this.events = events;
  }

  private el: HTMLAudioElement | null = null;
  private ctx: AudioContext | null = null;
  private trackGain: GainNode | null = null;
  private filters: BiquadFilterNode[] = [];
  private master: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private eqRouted = false;
  private wantPlaying = false;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;

  available(): boolean {
    return typeof window !== 'undefined' && typeof window.AudioContext === 'function';
  }

  private source: MediaElementAudioSourceNode | null = null;
  /** Слушатели `<audio>` — по именам, чтобы `dispose` мог их снять. */
  private elListeners: Array<[string, EventListener]> = [];
  private disposed = false;

  private ensure(): HTMLAudioElement {
    if (this.el) {
      return this.el;
    }
    const el = new Audio();
    el.preload = 'auto';
    const listen = (type: string, fn: EventListener) => {
      el.addEventListener(type, fn);
      this.elListeners.push([type, fn]);
    };
    listen('timeupdate', () => this.events.onPosition?.(el.currentTime));
    listen('ended', () => {
      this.wantPlaying = false;
      this.events.onEnded?.();
    });
    listen('error', () => {
      if (el.getAttribute('src')) {
        this.events.onError?.(new Error(el.error?.message || 'media error'));
      }
    });
    this.el = el;
    return el;
  }

  /**
   * Контекст — только по жесту пользователя (первое «играть»): созданный раньше, он стоит
   * в состоянии suspended по правилам автозапуска браузеров.
   */
  private ensureGraph(): void {
    if (this.ctx || !this.el) {
      return;
    }
    const ctx = new AudioContext({ latencyHint: 'playback' });
    const source = ctx.createMediaElementSource(this.el);
    const trackGain = ctx.createGain();
    const master = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.78;
    const filters = EQ_BAND_FREQUENCIES.map((f, i) => {
      const node = ctx.createBiquadFilter();
      node.type = filterTypeForBand(i);
      node.frequency.value = f;
      node.Q.value = EQ_PEAKING_Q;
      return node;
    });
    for (let i = 0; i + 1 < filters.length; i += 1) {
      filters[i].connect(filters[i + 1]);
    }
    filters[filters.length - 1].connect(master);
    source.connect(trackGain);
    trackGain.connect(master);
    master.connect(analyser);
    analyser.connect(ctx.destination);
    Object.assign(this, { ctx, source, trackGain, master, analyser, filters });
    this.setTrackGainDb(this.pendingTrackGain);
    this.setEqualizer(this.pendingEq);
  }

  private pendingEq: number[] | null = null;
  private pendingTrackGain = 0;

  load(uri: string, startAt: number, autoplay: boolean): boolean {
    if (this.disposed) {
      return false;
    }
    const el = this.ensure();
    if (el.src !== uri) {
      el.src = uri;
    }
    const start = () => {
      if (startAt > 0) {
        el.currentTime = startAt;
      }
      if (autoplay) {
        void this.play();
      }
    };
    if (el.readyState >= 1) {
      start();
    } else {
      el.addEventListener('loadedmetadata', start, { once: true });
    }
    return true;
  }

  async play(): Promise<void> {
    if (this.disposed) {
      return;
    }
    const el = this.ensure();
    this.ensureGraph();
    this.wantPlaying = true;
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
    if (this.ctx && this.master) {
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume().catch(() => undefined);
      }
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setValueAtTime(0, now);
      this.master.gain.setTargetAtTime(1, now, FADE_TAU);
    }
    try {
      await el.play();
    } catch (e) {
      this.wantPlaying = false;
      this.events.onError?.(e instanceof Error ? e : new Error(String(e)));
    }
  }

  pause(): void {
    this.wantPlaying = false;
    const el = this.el;
    if (!el) {
      return;
    }
    if (!this.ctx || !this.master) {
      el.pause();
      return;
    }
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(0, now, FADE_TAU);
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
    }
    this.pauseTimer = setTimeout(() => {
      this.pauseTimer = null;
      if (!this.wantPlaying) {
        el.pause();
      }
    }, FADE_SEC * 1000 + 20);
  }

  seek(sec: number): void {
    if (this.el) {
      this.el.currentTime = sec;
    }
  }

  stop(): void {
    this.wantPlaying = false;
    if (this.el) {
      this.el.pause();
      this.el.removeAttribute('src');
      this.el.load();
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.stop();
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
    const el = this.el;
    if (el) {
      this.elListeners.forEach(([type, fn]) => el.removeEventListener(type, fn));
    }
    this.elListeners = [];
    const nodes: Array<AudioNode | null> = [this.source, this.trackGain, ...this.filters, this.master, this.analyser];
    nodes.forEach((n) => {
      try {
        n?.disconnect();
      } catch {
        // ignore: узел уже отключён
      }
    });
    // Работающий AudioContext браузер не собирает: без close() каждый вход в аккаунт оставлял
    // по живому контексту — с его потоком и аудиовыходом.
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.source = null;
    this.trackGain = null;
    this.filters = [];
    this.master = null;
    this.analyser = null;
    this.el = null;
    this.events = {};
  }

  getPosition(): number {
    return this.el?.currentTime ?? 0;
  }

  setEqualizer(gains: number[] | null): void {
    this.pendingEq = gains;
    if (!this.ctx || !this.trackGain || !this.master) {
      return;
    }
    const now = this.ctx.currentTime;
    if (gains) {
      this.filters.forEach((f, i) => f.gain.setTargetAtTime(gains[i] ?? 0, now, 0.03));
    }
    const wantRouted = gains !== null;
    if (wantRouted !== this.eqRouted) {
      this.trackGain.disconnect();
      this.trackGain.connect(wantRouted ? this.filters[0] : this.master);
      this.eqRouted = wantRouted;
    }
  }

  setTrackGainDb(db: number): void {
    this.pendingTrackGain = db;
    if (this.ctx && this.trackGain) {
      this.trackGain.gain.setTargetAtTime(dbToLinear(db), this.ctx.currentTime, 0.05);
    }
  }

  spectrumBins(): number {
    return this.analyser?.frequencyBinCount ?? 0;
  }

  readSpectrum(out: Uint8Array): boolean {
    if (!this.analyser || out.length !== this.analyser.frequencyBinCount) {
      return false;
    }
    this.analyser.getByteFrequencyData(out as Uint8Array<ArrayBuffer>);
    return true;
  }
}

export function createMusicEngine(): MusicEngine {
  return new WebMusicEngine();
}

/** В браузере тегу не нужен React: `<audio>` создаётся движком и в дерево не встаёт. */
export function MusicEngineHost(_props: { engine: MusicEngine }): React.ReactElement | null {
  return null;
}

export function loadMusicApi(): null {
  return null;
}
